import React from 'react';
import { PD, SF } from './tokens';

type Variant = 'primary' | 'dark' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface BtnProps {
  variant?: Variant;
  size?: Size;
  icon?: string;
  onClick?: () => void;
  children: React.ReactNode;
}

const SIZES: Record<Size, { h: number; fs: number; px: number }> = {
  sm: { h: 30, fs: 12.5, px: 12 },
  md: { h: 36, fs: 13, px: 14 },
  lg: { h: 42, fs: 14, px: 18 }
};

function variantStyles(variant: Variant): { bg: string; fg: string; border: string } {
  switch (variant) {
    case 'primary':
      return { bg: PD.lime, fg: '#0a0a0a', border: 'none' };
    case 'dark':
      return { bg: PD.cta, fg: PD.ctaFg, border: 'none' };
    case 'secondary':
      return { bg: PD.card, fg: PD.ink, border: `0.5px solid ${PD.cardEdge}` };
    case 'ghost':
      return { bg: 'transparent', fg: PD.ink2, border: 'none' };
    case 'danger':
      return { bg: PD.card, fg: 'var(--pd-danger)', border: '0.5px solid rgba(239, 68, 68, 0.25)' };
  }
}

export const PdBtn: React.FC<BtnProps> = ({ variant = 'secondary', size = 'md', icon, onClick, children }) => {
  const s = SIZES[size];
  const v = variantStyles(variant);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: s.h,
        padding: `0 ${s.px}px`,
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        border: v.border,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        fontFamily: SF,
        fontSize: s.fs,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
      }}
    >
      {icon && <i className={`pi ${icon}`} style={{ fontSize: 13 }} />}
      {children}
    </button>
  );
};

interface PlanDetailsFooterProps {
  isEditing: boolean;
  canEditDelete: boolean;
  canStart: boolean;
  isClient: boolean;
  onCancel: () => void;
  onSave: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStart: () => void;
}

const PlanDetailsFooter: React.FC<PlanDetailsFooterProps> = ({
  isEditing,
  canEditDelete,
  canStart,
  isClient,
  onCancel,
  onSave,
  onEdit,
  onDelete,
  onStart
}) => {
  const hasAnyAction = isEditing || canEditDelete || canStart;
  if (!hasAnyAction) return null;

  return (
    <div
      style={{
        padding: '12px 18px',
        background: PD.footerTranslucent,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: `0.5px solid ${PD.hairline}`,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: '0 0 auto'
      }}
    >
      {isEditing ? (
        <>
          <PdBtn variant="secondary" onClick={onCancel}>
            Cancelar
          </PdBtn>
          <div style={{ flex: 1 }} />
          <PdBtn variant="dark" icon="pi-check" onClick={onSave}>
            Guardar cambios
          </PdBtn>
        </>
      ) : (
        <>
          {canEditDelete && (
            <PdBtn variant="danger" icon="pi-trash" onClick={onDelete}>
              Eliminar
            </PdBtn>
          )}
          <div style={{ flex: 1 }} />
          {canEditDelete && (
            <PdBtn variant="secondary" icon="pi-pencil" onClick={onEdit}>
              Editar ejercicios
            </PdBtn>
          )}
          {canStart &&
            (isClient ? (
              <PdBtn variant="primary" icon="pi-play" size="lg" onClick={onStart}>
                Iniciar sesión
              </PdBtn>
            ) : (
              <PdBtn variant="dark" icon="pi-play" onClick={onStart}>
                Iniciar sesión
              </PdBtn>
            ))}
        </>
      )}
    </div>
  );
};

export default PlanDetailsFooter;
